import { DocumentTextIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { useActiveStudents } from '@/components/scheduledLessonReport/StudentReports.hook';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import AvatarName from '@/components/ui/custom/dataDisplay/AvatarName';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import Popup from '@/components/ui/custom/Popup';
import useToggle from '@/data/hook/useToggle';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';
import { twMerge } from 'tailwind-merge';
import EnrollmentTag from '../enrollment/EnrollmentTag';
import { LoadingIcon } from '../icons';

type StudentsPopupProps = {
  klass: Pick<Klass, 'enrollsCount' | 'id'>;
  scheduledLesson?: Pick<ScheduledLesson, 'id' | 'datetime'>;
  className?: Pick<ClassNameComponent, 'ul'>;
};

export default function StudentsPopup({
  klass,
  scheduledLesson,
  className,
}: StudentsPopupProps) {
  const { t: tStudentsPopup } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.teacherKlasses',
  });

  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { id: klassId, enrollsCount } = klass;

  const { isOpen, close, open, toggle } = useToggle();

  const {
    enrollments,
    isInitialLoading: loading,
    error,
  } = useActiveStudents({
    klassId,
    scheduledLesson,
    enabled: isOpen,
  });

  return (
    <Popup
      opened={isOpen}
      onToggle={toggle}
      onClose={close}
      onOpen={open}
      reference={
        <span
          data-testid="studentPopupReference"
          className="flex text-primary items-center gap-x-1 cursor-pointer text-center h-fit"
        >
          <Text
            text={enrollsCount}
            format="rubik-500"
            className="text-[0.875rem] xl:text-[1rem]"
          />
          <UsersIcon className="h-3.5 stroke-[3]" />
        </span>
      }
    >
      <ul
        className={twMerge(
          'flex flex-col gap-1 bg-base-100 rounded-xl p-2 border border-neutral-content shadow-default',
          className?.ul,
        )}
      >
        <ConditionalRenderer condition={loading}>
          <div className="flex w-full justify-center">
            <LoadingIcon className="text-primary/40 w-10" />
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer condition={error}>
          <Text text={getErrorMessage(error)} />
        </ConditionalRenderer>

        <ConditionalRenderer condition={enrollments.length}>
          {enrollments.map(({ student, status }) => (
            <span
              data-testid="studentPopupItem"
              key={student.id}
              className="flex transition ease-in-out duration-500 gap-2 justify-start items-center truncate"
            >
              <AvatarName
                tooltip
                className="underline shrink text-primary font-500 grow-0 text-start truncate"
                textClassName="truncate"
                href={ROUTES.ADMIN.STUDENTS.INFO({
                  idIn: student.id.toString(),
                })}
                user={student}
              />

              <Tooltip text={tStudentsPopup('studentSheet')}>
                <IconButton
                  testId={`studentSheetLink${student.id}`}
                  href={ROUTES.ADMIN.STUDENTS.SHEET({
                    idIn: student.id.toString(),
                  })}
                  icon={<DocumentTextIcon className="text-primary w-6 h-6" />}
                />
              </Tooltip>
              <EnrollmentTag status={status} className="!text-12 px-1 py-0.5" />
            </span>
          ))}
        </ConditionalRenderer>

        <ConditionalRenderer
          condition={!enrollments.length && !loading && !error}
        >
          <Text
            text={`${t('noResults')} ¯\\_(ツ)_/¯`}
            className="overflow-hidden self-center text-primary"
          />
        </ConditionalRenderer>
      </ul>
    </Popup>
  );
}
